import { ChevronLeft, ChevronRight } from "lucide-react";
import { useRef } from "react";
import { useT } from "../../i18n/index.js";

/**
 * CEO 2026-10-31 ("os botões anterior e seguinte estão meio estranhos") —
 * paginação única das listas: resumo "1–50 de 137 faturas" à esquerda e, à
 * direita, Anterior · números de página · Seguinte, com a página atual
 * destacada. No celular os números viram "6 / 11". Mudar de página leva o
 * scroll de volta ao topo da lista.
 */
type Props = {
  total: number;
  pageSize: number;
  /** Posição do primeiro item da página atual (0, 50, 100…). */
  offset: number;
  onOffset: (offset: number) => void;
  /** Palavra depois do total ("faturas", "carros"…). */
  noun?: string;
  disabled?: boolean;
  testId?: string;
};

type Item = number | "gap-a" | "gap-b";

/** Primeira, última e a vizinhança da atual; o resto vira "…". */
function janela(atual: number, totalPaginas: number): Item[] {
  if (totalPaginas <= 7) return Array.from({ length: totalPaginas }, (_, i) => i + 1);
  const ini = Math.max(2, Math.min(atual - 1, totalPaginas - 4));
  const fim = Math.min(totalPaginas - 1, Math.max(atual + 1, 5));
  const itens: Item[] = [1];
  // "…" só quando esconde 2+ páginas; esconder uma só vira o próprio número.
  if (ini === 3) itens.push(2);
  else if (ini > 3) itens.push("gap-a");
  for (let p = ini; p <= fim; p++) itens.push(p);
  if (fim === totalPaginas - 2) itens.push(totalPaginas - 1);
  else if (fim < totalPaginas - 2) itens.push("gap-b");
  itens.push(totalPaginas);
  return itens;
}

function voltarAoTopo(el: HTMLElement | null) {
  const suave = !window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const behavior: ScrollBehavior = suave ? "smooth" : "auto";
  let p = el?.parentElement ?? null;
  while (p) {
    const s = getComputedStyle(p);
    if (/(auto|scroll)/.test(s.overflowY) && p.scrollHeight > p.clientHeight + 1) {
      p.scrollTo({ top: 0, behavior });
      return;
    }
    p = p.parentElement;
  }
  window.scrollTo({ top: 0, behavior });
}

const BTN =
  "inline-flex h-9 min-w-9 items-center justify-center gap-1 rounded-lg border px-2.5 text-sm font-semibold tabular-nums transition-colors " +
  "border-[var(--border-strong)] bg-[var(--surface-card)] text-[var(--text-strong)] " +
  "hover:enabled:border-lime hover:enabled:bg-[var(--surface-muted)] " +
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lime " +
  "disabled:cursor-not-allowed disabled:opacity-40";
const BTN_ATUAL =
  "inline-flex h-9 min-w-9 items-center justify-center rounded-lg border border-lime bg-lime px-2.5 text-sm font-bold tabular-nums text-on-lime";

export function Paginacao({
  total,
  pageSize,
  offset,
  onOffset,
  noun,
  disabled = false,
  testId = "pagination",
}: Props) {
  const t = useT();
  const ref = useRef<HTMLDivElement>(null);
  if (total <= 0) return null;
  const totalPaginas = Math.max(1, Math.ceil(total / pageSize));
  const atual = Math.min(totalPaginas, Math.floor(offset / pageSize) + 1);
  const de = offset + 1;
  const ate = Math.min(total, offset + pageSize);
  const ir = (pagina: number) => {
    if (pagina < 1 || pagina > totalPaginas || pagina === atual) return;
    onOffset((pagina - 1) * pageSize);
    voltarAoTopo(ref.current);
  };
  return (
    <div
      ref={ref}
      className="flex flex-wrap items-center justify-between gap-3 pt-1"
      data-testid={testId}
    >
      <p className="text-sm tabular-nums" style={{ color: "var(--text-muted)" }}>
        <span style={{ color: "var(--text-strong)", fontWeight: 600 }}>
          {de}–{ate}
        </span>{" "}
        {t("pagination.of")} {total}
        {noun ? ` ${noun}` : ""}
      </p>
      {totalPaginas > 1 && (
        <nav aria-label={t("pagination.aria")} className="flex items-center gap-1.5">
          <button
            type="button"
            className={BTN + " sm:pr-3.5"}
            onClick={() => ir(atual - 1)}
            disabled={disabled || atual <= 1}
            aria-label={t("pagination.prev")}
            data-testid={`${testId}-prev`}
          >
            <ChevronLeft size={16} aria-hidden="true" />
            <span className="hidden sm:inline">{t("pagination.prev")}</span>
          </button>
          <span className="hidden items-center gap-1 sm:flex">
            {janela(atual, totalPaginas).map((it) =>
              typeof it === "number" ? (
                it === atual ? (
                  <span
                    key={it}
                    className={BTN_ATUAL}
                    aria-current="page"
                    data-testid={`${testId}-current`}
                  >
                    {it}
                  </span>
                ) : (
                  <button
                    key={it}
                    type="button"
                    className={BTN}
                    onClick={() => ir(it)}
                    disabled={disabled}
                    aria-label={`${t("pagination.page")} ${it}`}
                    data-testid={`${testId}-page-${it}`}
                  >
                    {it}
                  </button>
                )
              ) : (
                <span
                  key={it}
                  className="px-1 text-sm"
                  style={{ color: "var(--text-muted)" }}
                  aria-hidden="true"
                >
                  …
                </span>
              ),
            )}
          </span>
          <span
            className="px-2 text-sm font-semibold tabular-nums sm:hidden"
            style={{ color: "var(--text-strong)" }}
          >
            {atual} / {totalPaginas}
          </span>
          <button
            type="button"
            className={BTN + " sm:pl-3.5"}
            onClick={() => ir(atual + 1)}
            disabled={disabled || atual >= totalPaginas}
            aria-label={t("pagination.next")}
            data-testid={`${testId}-next`}
          >
            <span className="hidden sm:inline">{t("pagination.next")}</span>
            <ChevronRight size={16} aria-hidden="true" />
          </button>
        </nav>
      )}
    </div>
  );
}
